import { Box, Button, useToast, CircularProgress } from '@chakra-ui/react';
import {
  DataTable,
  DataTableSortingRule,
} from 'features/common/components/DataTable';
import EmptyState from 'features/common/components/EmptyState';
import { tableOptions } from './utils';
import { useGetLogs } from './hooks/useGetLogs';
import {
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { AuditLog, AuditLogsResponse } from './types';
import { AxiosResponse } from 'axios';
import { AuditLogsSupportedSystemName } from './constants';
import { auditLogsApi } from './api/endpoints';

interface LoadMoreButtonProps {
  onClick: () => void;
}

const LoadMoreButton = ({ onClick }: LoadMoreButtonProps) => (
  <Box textAlign='center' mt={8} mb={16}>
    <Button
      variant='outline'
      aria-label='load more audit logs'
      size='sm'
      data-testid='load-more-audit-logs-button'
      onClick={onClick}
    >
      Load More
    </Button>
  </Box>
);

interface AuditLogsTableProps {
  system: AuditLogsSupportedSystemName;
  query?: string;
}
export const AuditLogsTable = forwardRef(
  (
    { system, query }: AuditLogsTableProps,
    ref: Ref<{ reloadData: () => void }>,
  ) => {
    const toast = useToast();
    const [continuationToken, setContinuationToken] = useState('');
    const [tableData, setTableData] = useState<AuditLog[]>([]);
    const [reloadLoading, setReloadLoading] = useState(false);
    const [sortByState, setSortByState] = useState<DataTableSortingRule[]>([
      { id: 'logTime', desc: true },
    ]);

    useEffect(() => {
      setContinuationToken('');
      setTableData([]);
    }, [system, query]);

    const onError = useCallback(() => {
      toast({
        description: 'Failed to load audit logs. Please try again.',
        title: 'Error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }, [toast]);

    const onSuccess = useCallback((res: AxiosResponse<AuditLogsResponse>) => {
      if (res.data.data.length) {
        setTableData((currTableData) => [...currTableData, ...res.data.data]);
      }
    }, []);

    const {
      data: response,
      isLoading,
      refetch,
    } = useGetLogs({
      onSuccess,
      onError,
      system,
      continuationToken,
      query,
    });

    const reloadData = useCallback(() => {
      setSortByState([{ id: 'logTime', desc: true }]);
      setReloadLoading(true);
      setContinuationToken('');
      setTableData([]);
      refetch({
        queryKey: [
          auditLogsApi.get_logs.url({ system, continuationToken: '', query }),
        ],
      }).finally(() => {
        setReloadLoading(false);
      });
    }, [refetch, system, query, setSortByState]);

    useImperativeHandle(ref, () => ({
      reloadData,
    }));

    const handleLoadMore = useCallback(() => {
      if (response?.data?.continuationToken) {
        setContinuationToken(response.data.continuationToken);
      }
    }, [response?.data?.continuationToken]);

    const responseData = response?.data;
    const initialRows: AuditLog[] = responseData?.data ?? [];
    const allRows = tableData.length > 0 ? tableData : initialRows;
    const filteredTableData = useMemo(() => {
      if (!query) return allRows;
      const lowerQuery = query.toLowerCase();
      return allRows.filter((log) => {
        const actor =
          log.sourceSystemUserContext?.id ??
          log.sourceSystemUserContext?.anonymousId ??
          '';
        const action = log.action ?? '';
        const user = log.entity?.id ?? '';
        const environment = log.environmentId ?? '';
        const roles = log.extensions?.roles;
        const rolesArray = Array.isArray(roles) ? roles : [];
        const role = rolesArray[0]?.role ?? '';
        const scope = rolesArray[0]?.scope ?? '';
        return [actor, action, user, environment, role, scope].some((field) =>
          field.toLowerCase().includes(lowerQuery),
        );
      });
    }, [allRows, query]);

    if (reloadLoading) {
      return (
        <Box
          flex='1'
          w='100%'
          display='flex'
          alignItems='center'
          justifyContent='center'
          data-testid='audit-logs-reloading-spinner'
        >
          <CircularProgress
            isIndeterminate
            capIsRound
            color='primary'
            trackColor='neutral-color.200'
          />
        </Box>
      );
    }

    if (!filteredTableData.length && !isLoading) {
      return (
        <EmptyState
          text='No audit logs for the selected product'
          dataTestId='no-audit-logs-container'
        />
      );
    }

    return (
      <>
        <DataTable
          columns={tableOptions.columns}
          data={filteredTableData}
          isLoading={!filteredTableData.length && isLoading}
          autoSort={false}
          sortBy={sortByState}
          onChange={({ sortBy }) => {
            const [primary] = sortBy;
            if (primary?.id && primary.id !== 'logTime') {
              setSortByState([primary, { id: 'logTime', desc: true }]);
            } else {
              setSortByState(sortBy);
            }
          }}
          page={1}
          pagination='hidden'
          data-testid='audit-logs-table'
          paginationProps={{
            'data-behavior-analytics-feature': 'audit logs pagination',
          }}
          data-behavior-analytics-feature='audit logs table'
        />
        {!!responseData?.continuationToken && (
          <LoadMoreButton onClick={handleLoadMore} />
        )}
      </>
    );
  },
);
